/**
 * Future SOS — multi-slot rescue planner.
 *
 * When a participant is marked unavailable for a future window, this module
 * identifies every assignment of theirs in the window and composes a single
 * batch plan that fills ALL vacated slots together. Batch composition is
 * scored by the full composite score (same scorer as the optimizer) and
 * pruned by admissible bounds — bounded disruption (depth ≤ 3 per slot)
 * with global quality.
 */

import { type CapabilityLossOverride, validateHardConstraints } from '../constraints/hard-constraints';
import { computeScheduleScore, type ScoreContext } from '../constraints/soft-constraints';
import {
  type Assignment,
  AssignmentStatus,
  type ConstraintViolation,
  type Participant,
  type RescueSwap,
  type Schedule,
  type SchedulerConfig,
  type SlotRequirement,
  type SplitOp,
  type Task,
} from '../models/types';
import { blocksOverlap } from '../shared/utils/time-utils';
import { isSchedulerDiagOn } from './diagnostics';
import { makeSplitHalf } from './optimizer';
import {
  type CandidateChain,
  type ChainEnumerationCaps,
  computeFallbackDepthUsed,
  deriveCapsForBatchSize,
  enumerateChainsForSlot,
  enumerateSplitFillsForSlot,
  type FallbackLevel,
  isSplitCandidate,
  type SlotCandidate,
  type SlotEnumerationContext,
} from './rescue-primitives';
import { isFutureTask, isModifiableAssignment } from './temporal';

// ─── Public Types ────────────────────────────────────────────────────────────

export interface FutureSosRequest {
  participantId: string;
  window: { start: Date; end: Date };
}

export interface AffectedAssignment {
  assignment: Assignment;
  task: Task;
  slot: SlotRequirement;
}

export interface PerParticipantChange {
  participantId: string;
  added: Assignment[];
  removed: Assignment[];
}

export interface BatchRescuePlan {
  id: string;
  rank: number;
  swaps: RescueSwap[];
  /**
   * Split-fill ops applied in addition to (or instead of) chain swaps. Each
   * op realizes one slot as `#a`/`#b` halves; see `SplitOp`. In Phase 1 a
   * given plan is mono-kind per affected slot — for that slot it has either
   * a chain entry in `swaps` OR a `SplitOp` in `splitOps`, not both.
   */
  splitOps?: SplitOp[];
  /** candidate composite − baseline composite. Positive = improvement. */
  compositeDelta: number;
  /** { 1: n1, 2: n2, 3: n3, 4: n4, 5: n5 } — chain depths used across all slots. */
  depthHistogram: Record<1 | 2 | 3 | 4 | 5, number>;
  perParticipantChanges: PerParticipantChange[];
  violations: ConstraintViolation[];
  /**
   * True when at least one affected slot had no candidate chain (so this plan
   * only fills the solvable subset). The UI must not let users apply a partial
   * plan and should clearly mark it as an illustrative preview.
   */
  isPartial: boolean;
  /**
   * Highest chain depth used in this plan when depth 4 or 5 was reached via
   * the deep-chain fallback cascade. Undefined for normal depth 1..3 plans.
   * The UI uses this to render a "fallback mode" banner.
   */
  fallbackDepthUsed?: 4 | 5;
  /**
   * Set by the capability-change planner when an "active focal-placement
   * extension" added swaps on top of the base chain composition specifically
   * to preserve the focal participant's workload after a cert loss. Plans
   * without an extension leave this `undefined` / `false`. The UI uses it
   * to render a "preserved focal workload" badge.
   */
  focalContinuityExtended?: boolean;
  /**
   * Effective hours added back to the focal participant by the extension
   * (sum of `computeTaskEffectiveHours` over extension-added assignments).
   * Only meaningful when `focalContinuityExtended === true`.
   */
  focalContinuityHoursAdded?: number;
}

export interface BatchRescueResult {
  request: FutureSosRequest;
  /** Assignments in the window that require a replacement. */
  affected: AffectedAssignment[];
  /** Assignments in the window that the user opted out of replacing. */
  excludedInWindow: AffectedAssignment[];
  /** Assignments in the window that are frozen (past) — shown for info only. */
  lockedInPast: AffectedAssignment[];
  /** Top-N plans, ranked by compositeDelta desc. */
  plans: BatchRescuePlan[];
  /** Assignment IDs for which no valid chain could be found. */
  infeasibleAssignmentIds: string[];
  /**
   * True when the DFS composition hit its wall-clock budget and returned
   * best-so-far. When true, `plans` may be empty or sub-optimal even if
   * there is no infeasibility — the caller should surface this to the user
   * so they can retry with a narrower window.
   */
  timedOut: boolean;
}

export interface GenerateBatchOpts {
  maxPlans?: number;
  caps?: ChainEnumerationCaps;
  disabledHC?: Set<string>;
  restRuleMap?: Map<string, number>;
  certLabelResolver?: (certId: string) => string;
  /** Budget in ms for the DFS composition stage. Default 500. */
  timeBudgetMs?: number;
  /**
   * Assignment IDs the user opted out of replacing. Still returned in
   * `excludedInWindow` for display, but the planner does not try to fill
   * them — the participant keeps the slot despite the window.
   */
  excludedAssignmentIds?: ReadonlySet<string>;
  config: SchedulerConfig;
  scoreCtx: ScoreContext;
  /** Schedule window context for HC-3 operational-day rule evaluation. */
  scheduleContext?: import('../shared/utils/time-utils').ScheduleContext;
}

// ─── Affected-set identification ────────────────────────────────────────────

/**
 * Find all assignments held by the participant that overlap the window.
 * Splits into modifiable (need rescue) vs past-frozen (informational only).
 */
export function findAffectedAssignments(
  schedule: Schedule,
  participantId: string,
  window: { start: Date; end: Date },
  anchor: Date,
): { affected: AffectedAssignment[]; lockedInPast: AffectedAssignment[] } {
  const taskMap = new Map<string, Task>();
  for (const t of schedule.tasks) taskMap.set(t.id, t);

  const affected: AffectedAssignment[] = [];
  const lockedInPast: AffectedAssignment[] = [];

  for (const a of schedule.assignments) {
    if (a.participantId !== participantId) continue;
    const task = taskMap.get(a.taskId);
    if (!task) continue;
    if (!blocksOverlap(task.timeBlock, window)) continue;
    const slot = task.slots.find((s) => s.slotId === a.slotId);
    if (!slot) continue;

    if (!isFutureTask(task, anchor) || !isModifiableAssignment(a, taskMap, anchor)) {
      lockedInPast.push({ assignment: a, task, slot });
    } else {
      affected.push({ assignment: a, task, slot });
    }
  }

  // Stable order: earliest task start first.
  affected.sort((a, b) => a.task.timeBlock.start.getTime() - b.task.timeBlock.start.getTime());
  lockedInPast.sort((a, b) => a.task.timeBlock.start.getTime() - b.task.timeBlock.start.getTime());
  return { affected, lockedInPast };
}

// ─── Composition helpers ────────────────────────────────────────────────────

/**
 * A composition is one chosen-candidate-per-slot selection. `slotIndex` maps
 * each entry of `slotCands` back to its ORIGINAL slot index — load-bearing for
 * callers (inject) that reorder slots for fail-fast DFS and must reconstruct
 * per-slot outcomes afterward. Callers that don't reorder (FSOS / cap-change)
 * ignore it; `dfsCompose` defaults it to identity.
 */
export type Composition = { slotCands: SlotCandidate[]; deltaSum: number; slotIndex: number[] };

/**
 * Project a chain-only subset out of a composition. Shared with
 * capability-change and inject (they previously inlined/forked this).
 */
export function chainsOf(cands: SlotCandidate[]): CandidateChain[] {
  return cands.filter((c): c is CandidateChain => !isSplitCandidate(c));
}

/** Project the SplitOp list out of a composition. */
export function splitOpsOf(cands: SlotCandidate[]): SplitOp[] {
  const out: SplitOp[] = [];
  for (const c of cands) if (isSplitCandidate(c)) out.push(c.splitOp);
  return out;
}

/**
 * Build the (tasks, assignments) that applying a plan's flat split + swap ops
 * to a schedule would produce, WITHOUT mutating the schedule. Pure counterpart
 * of `engine.applyPlanOps` and the shared core of `applyCompositionToState`.
 * Takes a plan's flat fields directly (`BatchRescuePlan.splitOps` /
 * `BatchRescuePlan.swaps`), so callers that only hold a plan — not the original
 * `SlotCandidate[]` — can still reconstruct the post-plan view (e.g.
 * capability-change's focal-continuity extension).
 *
 * Splits are applied first (rebuild tasks + assignment list); chain swaps then
 * run on the resulting assignment list with in-place participantId
 * substitution. Phase-1 plans are mono-kind per slot, so swaps never refer to a
 * half-task and split halves carry fresh synthetic assignment ids
 * (`${originalAssignmentId}#a` / `#b`). Returns the schedule's own arrays by
 * reference when nothing mutated, so callers' `result.tasks === schedule.tasks`
 * fast-paths stay valid.
 */
export function applyPlanOpsToState(
  schedule: Schedule,
  splitOps: SplitOp[],
  swaps: RescueSwap[],
): { tasks: Task[]; assignments: Assignment[] } {
  let tasks = schedule.tasks;
  let assignments = schedule.assignments;
  let mutatedTasks = false;
  let mutatedAsgs = false;

  for (const op of splitOps) {
    const T = tasks.find((t) => t.id === op.taskId);
    if (!T) continue;
    const slot = T.slots.find((s) => s.slotId === op.slotId);
    if (!slot) continue;
    const halfA = makeSplitHalf(T, 1, T.timeBlock.start.getTime(), op.midpointMs, slot);
    const halfB = makeSplitHalf(T, 2, op.midpointMs, T.timeBlock.end.getTime(), slot);
    const survivingSlots = T.slots.filter((s) => s.slotId !== op.slotId);
    const newTasks: Task[] = [];
    for (const t of tasks) {
      if (t.id === T.id) {
        if (survivingSlots.length > 0) {
          // Stamp sameGroupLinkId on residual for sameGroupRequired parents so
          // HC-8 link-union groups residual + halves as one unit.
          newTasks.push({
            ...T,
            slots: survivingSlots,
            requiredCount: survivingSlots.length,
            sameGroupLinkId: T.sameGroupRequired ? (T.sameGroupLinkId ?? T.id) : T.sameGroupLinkId,
          });
        }
        newTasks.push(halfA, halfB);
      } else {
        newTasks.push(t);
      }
    }
    tasks = newTasks;
    mutatedTasks = true;
    const newAsgs: Assignment[] = [];
    for (const a of assignments) {
      if (a.id === op.originalAssignmentId) continue;
      newAsgs.push(a);
    }
    const now = new Date();
    newAsgs.push({
      id: `${op.originalAssignmentId}#a`,
      taskId: halfA.id,
      slotId: halfA.slots[0].slotId,
      participantId: op.fillA.participantId,
      status: AssignmentStatus.Manual,
      updatedAt: now,
    });
    newAsgs.push({
      id: `${op.originalAssignmentId}#b`,
      taskId: halfB.id,
      slotId: halfB.slots[0].slotId,
      participantId: op.fillB.participantId,
      status: AssignmentStatus.Manual,
      updatedAt: now,
    });
    assignments = newAsgs;
    mutatedAsgs = true;
  }

  // Apply chain swaps last via in-place participantId substitution (does
  // not touch the task set). Single .map() over the (possibly already
  // split-mutated) assignments preserves the post-split synthetic halves.
  const swapMap = new Map<string, string>();
  for (const s of swaps) swapMap.set(s.assignmentId, s.toParticipantId);
  if (swapMap.size > 0) {
    assignments = assignments.map((a) => {
      const pid = swapMap.get(a.id);
      return pid ? { ...a, participantId: pid } : a;
    });
    mutatedAsgs = true;
  }

  return {
    tasks: mutatedTasks ? tasks : schedule.tasks,
    assignments: mutatedAsgs ? assignments : schedule.assignments,
  };
}

/**
 * Build the (tasks, assignments) the composition would produce if applied.
 * Used by post-composition validation/scoring so the trial state mirrors
 * what `engine.applyPlanOps` actually commits — including split-induced
 * task-set mutations.
 *
 * Thin adapter over `applyPlanOpsToState`: projects the composition's split
 * ops and chain swaps into flat lists. `chainsOf(...).flatMap(c => c.swaps)`
 * preserves candidate + within-chain order, so swap last-write-wins is
 * identical to the prior inline implementation. Phase-1 plans are mono-kind per
 * slot, so chains never refer to a half-task and split halves carry fresh
 * synthetic assignment ids.
 */
export function applyCompositionToState(
  schedule: Schedule,
  cands: SlotCandidate[],
): { tasks: Task[]; assignments: Assignment[] } {
  return applyPlanOpsToState(
    schedule,
    splitOpsOf(cands),
    chainsOf(cands).flatMap((c) => c.swaps),
  );
}

/**
 * 32-bit non-cryptographic hash over a plan's swap list — enough uniqueness
 * for plan ID stability (UI state, test equivalence) without pulling in a
 * real hash dep. Independent of time → identical inputs produce identical IDs.
 */
export function hashSwaps(swaps: RescueSwap[]): string {
  let h = 2166136261; // FNV-1a offset basis
  const key = swaps
    .map((s) => `${s.assignmentId}>${s.toParticipantId}`)
    .sort()
    .join('|');
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

/**
 * Maximal Marginal Relevance selection over a composite-ranked list — the
 * generic core shared by every batch planner (FSOS / capability-change via
 * `selectDiversePlans`, BALTAM injection via its own thin wrapper).
 *
 * Without this the top-K often collapses to near-duplicates (slot-0's best
 * candidate reused across the first several compositions, varying only in
 * slot-1). MMR picks the highest-scoring item first, then each subsequent
 * pick maximizes delta − λ · max-similarity-to-already-picked. Similarity is
 * Jaccard overlap of each item's `keyOf` fingerprint — two items with the same
 * people on the same tasks score ~1.0 and get demoted. λ = 0.3 keeps quality
 * primary but breaks tie-cluster dominance.
 *
 * `keyOf` extracts an item's similarity fingerprint (which (who|where) pairs it
 * touches); `deltaOf` extracts its composite delta. Callers vary only in these
 * two extractors — the selection math is identical.
 */
export function selectDiverseBy<T>(
  ranked: T[],
  k: number,
  lambda: number,
  keyOf: (item: T) => Set<string>,
  deltaOf: (item: T) => number,
): T[] {
  if (ranked.length <= k) return ranked;
  const keys = ranked.map(keyOf);
  const refDelta = Math.max(1, Math.abs(deltaOf(ranked[0])));
  const picked: number[] = [0];
  while (picked.length < k && picked.length < ranked.length) {
    let bestIdx = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < ranked.length; i++) {
      if (picked.includes(i)) continue;
      let maxSim = 0;
      for (const j of picked) {
        const a = keys[i];
        const b = keys[j];
        if (a.size === 0 && b.size === 0) continue;
        let inter = 0;
        for (const x of a) if (b.has(x)) inter++;
        const union = a.size + b.size - inter;
        const sim = union === 0 ? 0 : inter / union;
        if (sim > maxSim) maxSim = sim;
      }
      const s = deltaOf(ranked[i]) - lambda * maxSim * refDelta;
      if (s > bestScore) {
        bestScore = s;
        bestIdx = i;
      }
    }
    if (bestIdx === -1) break;
    picked.push(bestIdx);
  }
  return picked.map((i) => ranked[i]);
}

/** MMR selection keyed on each plan's swap (toParticipantId, taskId) pairs. */
export function selectDiversePlans(ranked: BatchRescuePlan[], k: number, lambda = 0.3): BatchRescuePlan[] {
  return selectDiverseBy(
    ranked,
    k,
    lambda,
    (p) => new Set(p.swaps.map((s) => `${s.toParticipantId}|${s.taskId}`)),
    (p) => p.compositeDelta,
  );
}

export function mergeSwaps(chains: CandidateChain[]): RescueSwap[] {
  const merged: RescueSwap[] = [];
  const seen = new Set<string>();
  for (const c of chains) {
    for (const s of c.swaps) {
      if (seen.has(s.assignmentId)) continue;
      seen.add(s.assignmentId);
      merged.push(s);
    }
  }
  return merged;
}

export function buildPerParticipantChanges(
  baseline: Assignment[],
  candidate: Assignment[],
  touchedAssignmentIds: Set<string>,
): PerParticipantChange[] {
  const baseById = new Map<string, Assignment>();
  for (const a of baseline) baseById.set(a.id, a);
  const candById = new Map<string, Assignment>();
  for (const a of candidate) candById.set(a.id, a);

  const byParticipant = new Map<string, { added: Assignment[]; removed: Assignment[] }>();
  const ensure = (pid: string) => {
    let entry = byParticipant.get(pid);
    if (!entry) {
      entry = { added: [], removed: [] };
      byParticipant.set(pid, entry);
    }
    return entry;
  };

  for (const id of touchedAssignmentIds) {
    const before = baseById.get(id);
    const after = candById.get(id);
    if (before && after) {
      if (before.participantId === after.participantId) continue;
      ensure(before.participantId).removed.push(before);
      ensure(after.participantId).added.push(after);
    } else if (before) {
      // Removal-only: the original assignment is gone (e.g. a split's original
      // occurrence replaced by its `#a`/`#b` halves). Count the focal's loss so
      // split plans report honest disruption (not 0) for the ranking tiebreaker.
      ensure(before.participantId).removed.push(before);
    } else if (after) {
      // Addition-only: a newly-created assignment (e.g. a split half) with no
      // baseline counterpart. Count the filler's gain.
      ensure(after.participantId).added.push(after);
    }
  }

  const result: PerParticipantChange[] = [];
  for (const [pid, v] of byParticipant) {
    result.push({ participantId: pid, added: v.added, removed: v.removed });
  }
  return result;
}

// ─── DFS composition with branch-and-bound ──────────────────────────────────

interface DfsState {
  chosen: (SlotCandidate | null)[];
  usedAssignmentIds: Set<string>;
  usedPairs: Set<string>; // "pid|taskId"
  sumSoloDelta: number;
}

function pairKey(participantId: string, taskId: string): string {
  return `${participantId}|${taskId}`;
}

export interface DfsOutcome {
  compositions: Composition[];
  timedOut: boolean;
}

export function dfsCompose(
  candidatesPerSlot: SlotCandidate[][],
  topK: number,
  deadline: number,
  /**
   * Original slot index of each entry in `candidatesPerSlot`. Callers that
   * reorder candidates for fail-fast DFS pass this so each composition's
   * `slotIndex` maps back to slot order; defaults to identity for callers that
   * never reorder (or never read `slotIndex`).
   */
  originalIndex: number[] = candidatesPerSlot.map((_, i) => i),
): DfsOutcome {
  // Suffix "best remaining" sums, for admissible upper-bound pruning.
  const suffixBest: number[] = new Array(candidatesPerSlot.length + 1).fill(0);
  for (let i = candidatesPerSlot.length - 1; i >= 0; i--) {
    const best = candidatesPerSlot[i].length > 0 ? candidatesPerSlot[i][0].soloCompositeDelta : -Infinity;
    suffixBest[i] = suffixBest[i + 1] + (Number.isFinite(best) ? best : 0);
  }

  const best: Composition[] = [];
  let timedOut = false;
  const state: DfsState = {
    chosen: new Array(candidatesPerSlot.length).fill(null),
    usedAssignmentIds: new Set(),
    usedPairs: new Set(),
    sumSoloDelta: 0,
  };

  function tryInsert(comp: Composition) {
    if (best.length < topK) {
      best.push(comp);
      best.sort((a, b) => b.deltaSum - a.deltaSum);
      return;
    }
    if (comp.deltaSum > best[best.length - 1].deltaSum) {
      best[best.length - 1] = comp;
      best.sort((a, b) => b.deltaSum - a.deltaSum);
    }
  }

  function worstKeptDelta(): number {
    return best.length < topK ? -Infinity : best[best.length - 1].deltaSum;
  }

  function recurse(i: number) {
    if (Date.now() > deadline) {
      timedOut = true;
      return;
    }

    if (i === candidatesPerSlot.length) {
      const slotCands = state.chosen.filter((c): c is SlotCandidate => c !== null);
      tryInsert({ slotCands, deltaSum: state.sumSoloDelta, slotIndex: [...originalIndex] });
      return;
    }

    // Admissible prune: even with the best possible remaining chains,
    // can this branch improve on the current worst kept?
    const upperBound = state.sumSoloDelta + suffixBest[i];
    if (upperBound <= worstKeptDelta()) return;

    const cands = candidatesPerSlot[i];
    for (const cand of cands) {
      if (Date.now() > deadline) {
        timedOut = true;
        return;
      }

      // Conflict checks.
      let conflict = false;
      for (const id of cand.touchedAssignmentIds) {
        if (state.usedAssignmentIds.has(id)) {
          conflict = true;
          break;
        }
      }
      if (conflict) continue;
      for (const pair of cand.participantTaskPairs) {
        if (state.usedPairs.has(pairKey(pair.participantId, pair.taskId))) {
          conflict = true;
          break;
        }
      }
      if (conflict) continue;

      // Advance.
      const addedAssn: string[] = [];
      const addedPairs: string[] = [];
      for (const id of cand.touchedAssignmentIds) {
        state.usedAssignmentIds.add(id);
        addedAssn.push(id);
      }
      for (const pair of cand.participantTaskPairs) {
        const k = pairKey(pair.participantId, pair.taskId);
        state.usedPairs.add(k);
        addedPairs.push(k);
      }
      state.chosen[i] = cand;
      state.sumSoloDelta += cand.soloCompositeDelta;

      recurse(i + 1);

      // Backtrack.
      state.sumSoloDelta -= cand.soloCompositeDelta;
      state.chosen[i] = null;
      for (const id of addedAssn) state.usedAssignmentIds.delete(id);
      for (const k of addedPairs) state.usedPairs.delete(k);
    }
  }

  recurse(0);
  return { compositions: best, timedOut };
}

// ─── Shared composer budget / breadth constants ─────────────────────────────
// These five values are the "Shared composer invariants" CLAUDE.md documents as
// ONE rule. They live here (single source) rather than being re-derived inline
// in each composer's runStage (FSOS / capability-change / inject), so a tuning
// change cannot silently desync across the three flows. Mirrors the
// depth3DonorCaps de-forking precedent (a verbatim-copied formula extracted to
// one helper to kill drift).

/** Proxy-ranked top-K composition pool for the first compose pass. */
export const composerBaseTopK = (maxPlans: number): number => Math.max(maxPlans * 4, 8);
/** Wider pool for the gated breadth-retry (one re-validation at the same depth). */
export const composerRetryTopK = (maxPlans: number): number => Math.max(maxPlans * 12, 36);
/** Wall-clock cap (ms) on the breadth-retry's own DFS budget. */
export const COMPOSER_RETRY_BUDGET_MS = 3000;
/** Hard ceiling (ms) on a single deep-chain fallback stage's DFS budget. */
export const COMPOSER_FALLBACK_MAX_BUDGET_MS = 10_000;
/** Fallback-stage budget: 4× the base budget, capped at the ceiling. */
export const composerFallbackBudget = (baseBudgetMs: number): number =>
  Math.min(COMPOSER_FALLBACK_MAX_BUDGET_MS, baseBudgetMs * 4);

// ─── Shared compose + validate + score ──────────────────────────────────────

/**
 * Validate+score inputs shared by the FSOS and capability-change runStage
 * closures. Both build the SAME `BatchRescuePlan` from each `Composition`;
 * they differ only in `idPrefix` (the plan-id namespace).
 */
export interface BatchComposeCtx {
  schedule: Schedule;
  /** Per-slot candidate pools, already fail-fast-ordered for the DFS. */
  orderedCandidates: SlotCandidate[][];
  /** Baseline composite score the candidate deltas are measured against. */
  baselineComposite: number;
  /** True when ≥1 affected slot had no candidate (plan fills the solvable subset). */
  isPartial: boolean;
  /** Plan-id namespace prefix: 'fsos' | 'capch'. */
  idPrefix: string;
  config: SchedulerConfig;
  scoreCtx: ScoreContext;
  disabledHC?: Set<string>;
  restRuleMap?: Map<string, number>;
  certLabelResolver?: (certId: string) => string;
  scheduleContext?: import('../shared/utils/time-utils').ScheduleContext;
  extraUnavailability?: Array<{ participantId: string; start: Date; end: Date }>;
  extraCapabilityLoss?: CapabilityLossOverride[];
}

/**
 * Compose at `topK` (proxy-ranked by sum-of-solo-deltas) via `dfsCompose`, then
 * HC-validate + score each composition into valid (`scored`) / invalid
 * (`scoredInvalid`) `BatchRescuePlan` buckets.
 *
 * Formerly two byte-identical inline copies (FSOS + capability-change) differing
 * only in the plan-id prefix; extracted so the gated breadth-retry in each
 * caller can re-run it at a wider `topK` without duplication, and so the subtle
 * split-aware `trialScoreCtx` taskMap rebuild lives in ONE place. Inject keeps
 * its own `composeToPlan` (genuine variant: per-slot outcomes / partial plans).
 */
export function composeAndScoreBatch(
  ctx: BatchComposeCtx,
  topK: number,
  deadline: number,
): {
  compositions: Composition[];
  scored: BatchRescuePlan[];
  scoredInvalid: BatchRescuePlan[];
  timedOut: boolean;
} {
  const { schedule, baselineComposite, isPartial, idPrefix } = ctx;
  const { compositions, timedOut } = dfsCompose(ctx.orderedCandidates, topK, deadline);
  const scored: BatchRescuePlan[] = [];
  const scoredInvalid: BatchRescuePlan[] = [];
  for (let i = 0; i < compositions.length; i++) {
    const comp = compositions[i];
    const touched = new Set<string>();
    for (const c of comp.slotCands) {
      for (const id of c.touchedAssignmentIds) touched.add(id);
      // A split candidate's touchedAssignmentIds is just the original (now
      // removed) id; also track the synthetic `#a`/`#b` halves so
      // buildPerParticipantChanges counts the two fillers' gains (otherwise a
      // pure-split plan reports 0 disturbance and unfairly wins the tiebreaker).
      if (isSplitCandidate(c) && c.splitOp.originalAssignmentId) {
        touched.add(`${c.splitOp.originalAssignmentId}#a`);
        touched.add(`${c.splitOp.originalAssignmentId}#b`);
      }
    }

    const trial = applyCompositionToState(schedule, comp.slotCands);

    const validation = validateHardConstraints(
      trial.tasks,
      schedule.participants,
      trial.assignments,
      ctx.disabledHC,
      ctx.restRuleMap,
      ctx.certLabelResolver,
      ctx.extraUnavailability,
      ctx.scheduleContext,
      ctx.extraCapabilityLoss,
    );

    const depthHistogram: Record<1 | 2 | 3 | 4 | 5, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (const c of comp.slotCands) {
      if (!isSplitCandidate(c)) depthHistogram[c.depth]++;
    }
    const fallbackDepthUsed = computeFallbackDepthUsed(comp.slotCands);
    const chainSubset = chainsOf(comp.slotCands);
    const splitOpsForPlan = splitOpsOf(comp.slotCands);

    if (validation.violations.length > 0) {
      scoredInvalid.push({
        id: `${idPrefix}-${hashSwaps(mergeSwaps(chainSubset))}-${i}`,
        rank: 0,
        swaps: mergeSwaps(chainSubset),
        splitOps: splitOpsForPlan.length > 0 ? splitOpsForPlan : undefined,
        compositeDelta: comp.deltaSum,
        depthHistogram,
        perParticipantChanges: buildPerParticipantChanges(schedule.assignments, trial.assignments, touched),
        violations: validation.violations,
        isPartial,
        fallbackDepthUsed,
      });
      continue;
    }

    // Override `scoreCtx.taskMap` for the trial: ctx.scoreCtx was built over the
    // PRE-split task set, so its taskMap is missing the new `#a`/`#b` half-task
    // ids. computeScheduleScore consults `ctx.taskMap` in preference to the
    // `tasks` arg, so without this rebuild the per-assignment soft-score loops
    // skip the halves and the composite delta loses fragment-honest scoring.
    const trialScoreCtx =
      trial.tasks === schedule.tasks
        ? ctx.scoreCtx
        : {
            ...ctx.scoreCtx,
            taskMap: new Map(trial.tasks.map((t) => [t.id, t])),
            assignmentsByParticipant: undefined,
            assignmentsByTask: undefined,
          };
    const candScore = computeScheduleScore(
      trial.tasks,
      schedule.participants,
      trial.assignments,
      ctx.config,
      trialScoreCtx,
    );
    const compositeDelta = candScore.compositeScore - baselineComposite;

    scored.push({
      id: `${idPrefix}-${hashSwaps(mergeSwaps(chainSubset))}-${i}`,
      rank: 0,
      swaps: mergeSwaps(chainSubset),
      splitOps: splitOpsForPlan.length > 0 ? splitOpsForPlan : undefined,
      compositeDelta,
      depthHistogram,
      perParticipantChanges: buildPerParticipantChanges(schedule.assignments, trial.assignments, touched),
      violations: validation.violations,
      isPartial,
      fallbackDepthUsed,
    });
  }
  return { compositions, scored, scoredInvalid, timedOut };
}

// ─── Effective-window helper ────────────────────────────────────────────────

/**
 * Subtract a set of kept-task intervals from a requested window, returning
 * the (possibly-split, possibly-empty) remaining windows.
 *
 * Used to punch holes in focal's Future-SOS unavailability around assignments
 * the user chose to keep (opt-out). Without this, the final validator fires
 * HC-3 on every kept in-window assignment even though the user explicitly
 * said "leave this one with focal."
 */
export function computeEffectiveUnavailabilityWindows(
  window: { start: Date; end: Date },
  keptBlocks: Array<{ start: Date; end: Date }>,
): Array<{ start: Date; end: Date }> {
  let current: Array<{ start: Date; end: Date }> = [{ start: window.start, end: window.end }];
  for (const kept of keptBlocks) {
    const next: Array<{ start: Date; end: Date }> = [];
    for (const w of current) {
      // No overlap → keep whole.
      if (kept.end.getTime() <= w.start.getTime() || kept.start.getTime() >= w.end.getTime()) {
        next.push(w);
        continue;
      }
      // Left slice before the kept block, if any.
      if (kept.start.getTime() > w.start.getTime()) {
        next.push({ start: w.start, end: kept.start });
      }
      // Right slice after the kept block, if any.
      if (kept.end.getTime() < w.end.getTime()) {
        next.push({ start: kept.end, end: w.end });
      }
    }
    current = next;
  }
  return current;
}

// ─── Public entry point ─────────────────────────────────────────────────────

export function generateBatchRescuePlans(
  schedule: Schedule,
  request: FutureSosRequest,
  anchor: Date,
  opts: GenerateBatchOpts,
): BatchRescueResult {
  const { affected: allAffected, lockedInPast } = findAffectedAssignments(
    schedule,
    request.participantId,
    request.window,
    anchor,
  );

  const maxPlans = opts.maxPlans ?? 3;
  // K-adaptive caps: for the common K=2..4 case the search has plenty of
  // budget, so widen per-slot enumeration there. The historical fixed
  // {6,4,2} only protects worst-case K≥8; using it everywhere left K=2..4
  // batches under-explored. `deriveCapsForBatchSize` converges to the old
  // values at K≥8, so K≥8 behaviour is preserved.
  const affectedCount = allAffected.filter((a) => !opts.excludedAssignmentIds?.has(a.assignment.id)).length;
  const caps = opts.caps ?? deriveCapsForBatchSize(affectedCount);
  // K-aware time budget: smooth linear scaling instead of the historical
  // step at K=5. K=2 → 500ms, K=5 → 1250ms, K=8 → 2000ms, K≥14 → 4000ms
  // (clamped). DFS uses suffix-best admissible pruning so the extra budget
  // is spent on real branches, not fan-out.
  const defaultBudget = Math.min(4000, Math.max(500, 500 + 250 * Math.max(0, affectedCount - 2)));
  const timeBudgetMs = opts.timeBudgetMs ?? defaultBudget;
  // Wall-clock start for the honest total-time signal. The DFS deadline only
  // bounds dfsCompose; per-slot enumeration (which can dominate on large/tight
  // batches) runs uncounted. We OR an elapsed-vs-total-budget check into the
  // returned `timedOut` below so the UI's "narrow the window" affordance fires
  // on the real bottleneck instead of mislabelling a slow run as "infeasible".
  const opStart = Date.now();

  const excludedIds = opts.excludedAssignmentIds;
  const excludedInWindow: AffectedAssignment[] = [];
  const affected: AffectedAssignment[] = [];
  for (const a of allAffected) {
    if (excludedIds?.has(a.assignment.id)) excludedInWindow.push(a);
    else affected.push(a);
  }

  if (affected.length === 0) {
    return {
      request,
      affected,
      excludedInWindow,
      lockedInPast,
      plans: [],
      infeasibleAssignmentIds: [],
      timedOut: false,
    };
  }

  const taskMap = new Map<string, Task>();
  for (const t of schedule.tasks) taskMap.set(t.id, t);
  const participantMap = new Map<string, Participant>();
  for (const p of schedule.participants) participantMap.set(p.id, p);

  const assignmentsByParticipant = new Map<string, Assignment[]>();
  const assignmentsByTask = new Map<string, Assignment[]>();
  for (const a of schedule.assignments) {
    let l = assignmentsByParticipant.get(a.participantId);
    if (!l) {
      l = [];
      assignmentsByParticipant.set(a.participantId, l);
    }
    l.push(a);
    let tl = assignmentsByTask.get(a.taskId);
    if (!tl) {
      tl = [];
      assignmentsByTask.set(a.taskId, tl);
    }
    tl.push(a);
  }

  // Build extraUnavailability for eligibility checks + final validation:
  //   1. Start from the requested window.
  //   2. Subtract kept-task timeBlocks (opt-out) so focal keeps HC-3 coverage
  //      for assignments the user chose to leave in place — otherwise every
  //      returned plan would carry phantom HC-3 violations on those kept slots.
  //   3. Layer in any existing persisted scheduleUnavailability — without this,
  //      candidate eligibility ignores prior FSOS windows for any participant
  //      and the planner can place a candidate (or reassign focal) onto a task
  //      that's already inside a previously-recorded unavailability.
  //   4. Subtract lockedInPast task blocks too: a window that begins while the
  //      focal is mid-shift (a task straddling the anchor → lockedInPast) leaves
  //      the focal genuinely committed to that intrinsically-unmodifiable shift.
  //      Without this, the focal's effective unavailability still covers it and
  //      the final HC-3 validator fires AVAILABILITY_VIOLATION on the focal's own
  //      frozen assignment in EVERY composition → zero valid plans for the most
  //      common live scenario ("mark X unavailable from now"). You cannot be
  //      unavailable for a shift you are already performing — identical treatment
  //      to opt-out kept blocks, so no HC is relaxed.
  const keptTaskBlocks = [...excludedInWindow, ...lockedInPast].map((e) => e.task.timeBlock);
  const effectiveFocalWindows = computeEffectiveUnavailabilityWindows(request.window, keptTaskBlocks);
  const extraUnavailability: Array<{ participantId: string; start: Date; end: Date }> = [
    ...(schedule.scheduleUnavailability ?? []).map((u) => ({
      participantId: u.participantId,
      start: u.start,
      end: u.end,
    })),
    ...effectiveFocalWindows.map((w) => ({
      participantId: request.participantId,
      start: w.start,
      end: w.end,
    })),
  ];

  // Schedule-scoped capability losses (from a prior capability-change). The
  // apply-time validator (engine.validate / revalidateFull) enforces these via
  // the 9th `extraCapabilityLoss` arg, so FSOS MUST too — otherwise a cert-lost
  // participant is treated as still-certified during enumeration, a plan placing
  // them on a cert-gated slot is surfaced as "recommended", then HC-2 fires at
  // apply and the whole plan rolls back. Mirrors capability-change.ts/inject.ts.
  // Inert (byte-identical) when schedule.capabilityLoss is empty.
  const extraCapabilityLoss = (schedule.capabilityLoss ?? []).map((c) => ({
    participantId: c.participantId,
    lostCertifications: c.lostCertifications,
    start: c.start,
    end: c.end,
  }));

  const baselineScore = computeScheduleScore(
    schedule.tasks,
    schedule.participants,
    schedule.assignments,
    opts.config,
    opts.scoreCtx,
  );

  const slotCtx: SlotEnumerationContext = {
    schedule,
    taskMap,
    participantMap,
    assignmentsByParticipant,
    assignmentsByTask,
    anchor,
    disabledHC: opts.disabledHC,
    restRuleMap: opts.restRuleMap,
    scheduleContext: opts.scheduleContext,
    config: opts.config,
    scoreCtx: opts.scoreCtx,
    baselineComposite: baselineScore.compositeScore,
    extraUnavailability,
    extraCapabilityLoss,
    excludeParticipantIds: new Set([request.participantId]),
    // The focal's vacated slots: when the focal is reassigned into a
    // donor-receiver role, the enumerator strips these from its eligibility
    // list so HC-5/12/14/15 model the post-plan freed state (not a phantom
    // double-booking on slots this batch is vacating). `affected` excludes the
    // focal's kept opt-out (`excludedInWindow`) and `lockedInPast` blocks, which
    // must remain real blockers — only genuinely-vacated slots are stripped.
    focalVacatedAssignmentIds: new Set(affected.map((a) => a.assignment.id)),
  };

  // Inner helper — runs enumeration + DFS composition + scoring at a given
  // fallback level. Factored out so the deep-chain cascade can invoke it
  // multiple times without duplicating the scoring block.
  //
  // Returns:
  //  - validPlans: ranked & diversity-selected (never includes invalid plans)
  //  - invalidPlans: kept as a last-resort bucket across the whole cascade
  //  - infeasibleAssignmentIds: slots that had no chain at this fallback level
  //  - timedOut: whether DFS hit its wall-clock deadline
  //  - anySolvable: false iff every slot had zero chains (nothing to compose)
  const runStage = (
    fallbackLevel: FallbackLevel,
    budgetMs: number,
  ): {
    validPlans: BatchRescuePlan[];
    invalidPlans: BatchRescuePlan[];
    infeasibleIds: string[];
    timedOut: boolean;
    anySolvable: boolean;
  } => {
    const candidatesPerSlot: SlotCandidate[][] = [];
    const infeasibleIds: string[] = [];
    const splittingMode = schedule.algorithmSettings?.splittingMode ?? 'quality';
    for (const v of affected) {
      const chains = enumerateChainsForSlot(slotCtx, v.assignment, caps, fallbackLevel);
      // Dual-enumerator: optionally add split-fill candidates for this slot.
      // Per the design rule, splits are gated by per-task `splittable` and the
      // global `splittingMode`. The enumerator returns [] fast when the task
      // is not splittable, so the cost is identity-zero when splitting is off
      // or no template opts in.
      const splits =
        splittingMode !== 'off' && v.task.splittable
          ? enumerateSplitFillsForSlot(slotCtx, v.assignment, affectedCount)
          : [];
      const slotPool: SlotCandidate[] = [...chains, ...splits];
      // Sort the combined pool by descending composite delta so suffix-best
      // admissible pruning in dfsCompose sees the strongest candidate first.
      slotPool.sort((a, b) => b.soloCompositeDelta - a.soloCompositeDelta);
      if (slotPool.length === 0) infeasibleIds.push(v.assignment.id);
      candidatesPerSlot.push(slotPool);
    }

    const solvableIdx: number[] = [];
    for (let i = 0; i < candidatesPerSlot.length; i++) {
      if (candidatesPerSlot[i].length > 0) solvableIdx.push(i);
    }
    if (solvableIdx.length === 0) {
      if (isSchedulerDiagOn()) {
        console.log(
          `[future-sos] stage=${fallbackLevel} K=${affectedCount} caps=${JSON.stringify(caps)} ` +
            `no-solvable-slots infeasible=${infeasibleIds.length}/${affected.length}`,
        );
      }
      return { validPlans: [], invalidPlans: [], infeasibleIds, timedOut: false, anySolvable: false };
    }

    // Order slots by ascending candidate count (fail-fast DFS).
    const orderedSolvable = [...solvableIdx].sort((a, b) => candidatesPerSlot[a].length - candidatesPerSlot[b].length);
    const orderedCandidates = orderedSolvable.map((i) => candidatesPerSlot[i]);

    const isPartial = infeasibleIds.length > 0;

    // Compose at `topK` then HC-validate each composition into the valid
    // (`scored`) / invalid buckets via the shared `composeAndScoreBatch`. The
    // closure binds the per-stage context once so the gated breadth-retry below
    // can re-run it at a wider topK with no dup.
    const composeCtx: BatchComposeCtx = {
      schedule,
      orderedCandidates,
      baselineComposite: baselineScore.compositeScore,
      isPartial,
      idPrefix: 'fsos',
      config: opts.config,
      scoreCtx: opts.scoreCtx,
      disabledHC: opts.disabledHC,
      restRuleMap: opts.restRuleMap,
      certLabelResolver: opts.certLabelResolver,
      scheduleContext: opts.scheduleContext,
      extraUnavailability,
      extraCapabilityLoss,
    };
    const composeAndScore = (topK: number, deadline: number) => composeAndScoreBatch(composeCtx, topK, deadline);

    const baseTopK = composerBaseTopK(maxPlans);
    let { compositions, scored, scoredInvalid, timedOut } = composeAndScore(baseTopK, Date.now() + budgetMs);

    // Gated breadth-retry. The proxy top-K pool was FULL yet every composition
    // failed HC validation (cross-chain HC-5/12/14/15 are invisible to per-slot
    // solo enumeration and to dfsCompose's assignment/HC-7 conflict checks), and
    // the search did not time out — so a feasible lower-proxy composition may sit
    // just past the cutoff. Re-validate a WIDER pool ONCE at the same depth before
    // the depth cascade escalates (which only adds lower-proxy deeper chains and
    // never widens breadth). Never relaxes an HC — every surfaced plan is still
    // fully validated. Common-case byte-identical: skipped whenever the first pass
    // produced a valid plan, returned a non-full pool (no truncation), or timed out.
    if (scored.length === 0 && compositions.length >= baseTopK && !timedOut) {
      const retryTopK = composerRetryTopK(maxPlans);
      const retry = composeAndScore(retryTopK, Date.now() + Math.min(budgetMs, COMPOSER_RETRY_BUDGET_MS));
      if (isSchedulerDiagOn()) {
        console.log(
          `[future-sos] breadth-retry stage=${fallbackLevel} K=${affectedCount} ` +
            `baseTopK=${baseTopK} retryTopK=${retryTopK} recovered=${retry.scored.length} ` +
            `retryTimedOut=${retry.timedOut}`,
        );
      }
      compositions = retry.compositions;
      scored = retry.scored;
      scoredInvalid = retry.scoredInvalid;
      timedOut = timedOut || retry.timedOut;
    }

    if (compositions.length === 0) {
      if (isSchedulerDiagOn()) {
        console.log(
          `[future-sos] stage=${fallbackLevel} K=${affectedCount} caps=${JSON.stringify(caps)} ` +
            `budgetMs=${budgetMs} dfs-empty timedOut=${timedOut}`,
        );
      }
      return { validPlans: [], invalidPlans: [], infeasibleIds, timedOut, anySolvable: true };
    }

    // Rank valid plans; leave invalid ones unranked (caller may mix them in only
    // if no valid composition survived anywhere across the cascade).
    scored.sort((a, b) => {
      const delta = b.compositeDelta - a.compositeDelta;
      if (Math.abs(delta) > 0.1) return delta;
      const aDisturbed = a.perParticipantChanges.length;
      const bDisturbed = b.perParticipantChanges.length;
      if (aDisturbed !== bDisturbed) return aDisturbed - bDisturbed;
      return a.swaps.length - b.swaps.length;
    });

    const top = selectDiversePlans(scored, maxPlans);
    for (let i = 0; i < top.length; i++) top[i].rank = i + 1;

    if (isSchedulerDiagOn()) {
      console.log(
        `[future-sos] stage=${fallbackLevel} K=${affectedCount} caps=${JSON.stringify(caps)} ` +
          `budgetMs=${budgetMs} valid=${top.length} invalid=${scoredInvalid.length} ` +
          `infeasible=${infeasibleIds.length}/${affected.length} timedOut=${timedOut}`,
      );
    }

    return { validPlans: top, invalidPlans: scoredInvalid, infeasibleIds, timedOut, anySolvable: true };
  };

  // Deep-chain fallback cascade — feasibility-only escalation.
  // Stage 1: normal depth 1..3. Stage 2: depth 4 enumeration per slot (unlocks
  // slots that had zero chains AND gives DFS extra breadth to break
  // compositional deadlocks). Stage 3: depth 5 as last-resort.
  // We escalate only when the previous stage produced no valid plans; fallback
  // never overrides a feasible shallow-chain result.
  const fallbackBudget = composerFallbackBudget(timeBudgetMs);

  // Hard-constraint invariant: HC-violating plans are never surfaced. If no
  // valid plan is found at any depth, return an empty plans list and let the
  // UI present the infeasible-state modal — never promote invalidPlans here.
  const stage1 = runStage('none', timeBudgetMs);
  let finalPlans = stage1.validPlans;
  let finalInfeasible = stage1.infeasibleIds;
  let finalTimedOut = stage1.timedOut;
  let stageAnySolvable = stage1.anySolvable;

  if (finalPlans.length === 0 && (stage1.anySolvable || stage1.infeasibleIds.length > 0)) {
    console.warn(
      `[future-sos] depth-4 fallback fired for participant ${request.participantId} ` +
        `(window ${request.window.start.toISOString()}..${request.window.end.toISOString()}, ` +
        `infeasibleSlots=${stage1.infeasibleIds.length}/${affected.length})`,
    );
    const stage2 = runStage('depth4', fallbackBudget);
    finalPlans = stage2.validPlans;
    finalInfeasible = stage2.infeasibleIds;
    finalTimedOut = stage2.timedOut;
    stageAnySolvable = stage2.anySolvable;
  }

  if (finalPlans.length === 0 && (stageAnySolvable || finalInfeasible.length > 0)) {
    console.warn(
      `[future-sos] depth-5 fallback fired for participant ${request.participantId} ` +
        `(window ${request.window.start.toISOString()}..${request.window.end.toISOString()}, ` +
        `infeasibleSlots=${finalInfeasible.length}/${affected.length})`,
    );
    const stage3 = runStage('depth5', fallbackBudget);
    finalPlans = stage3.validPlans;
    finalInfeasible = stage3.infeasibleIds;
    finalTimedOut = stage3.timedOut;
  }

  // Honest total-time: if the whole operation (enumeration + every stage)
  // overran the worst-case budget we'd ever intend to spend, report timedOut so
  // the caller surfaces "timed out — try a narrower window" rather than a flat
  // "infeasible". `timeBudgetMs + 2 * fallbackBudget` is the sum of all three
  // stages' DFS budgets; exceeding it means enumeration (uncounted by the DFS
  // deadline) was the real cost. Tiny/fast inputs never reach it → unchanged.
  const totalBudgetMs = timeBudgetMs + 2 * fallbackBudget;
  if (Date.now() - opStart > totalBudgetMs) finalTimedOut = true;

  return {
    request,
    affected,
    excludedInWindow,
    lockedInPast,
    plans: finalPlans,
    infeasibleAssignmentIds: finalInfeasible,
    timedOut: finalTimedOut,
  };
}

// ─── Entry persistence ──────────────────────────────────────────────────────

/**
 * Append a new ScheduleUnavailability entry for a participant, merging
 * with any existing overlapping entry (windows unioned).
 *
 * Returns the updated scheduleUnavailability array (new reference).
 */
export function upsertScheduleUnavailability(
  existing: NonNullable<Schedule['scheduleUnavailability']> | undefined,
  entry: {
    id: string;
    participantId: string;
    start: Date;
    end: Date;
    reason?: string;
    createdAt: Date;
    anchorAtCreation: Date;
    appliedSwapCount?: number;
  },
): NonNullable<Schedule['scheduleUnavailability']> {
  const result = (existing ?? []).map((e) => ({ ...e }));
  let mergedStart = entry.start;
  let mergedEnd = entry.end;
  const overlappingIdx: number[] = [];
  for (let i = 0; i < result.length; i++) {
    const r = result[i];
    if (r.participantId !== entry.participantId) continue;
    if (mergedStart.getTime() < r.end.getTime() && r.start.getTime() < mergedEnd.getTime()) {
      overlappingIdx.push(i);
      if (r.start.getTime() < mergedStart.getTime()) mergedStart = r.start;
      if (r.end.getTime() > mergedEnd.getTime()) mergedEnd = r.end;
    }
  }
  // Remove overlapping entries (back-to-front so indexes stay valid).
  for (let i = overlappingIdx.length - 1; i >= 0; i--) result.splice(overlappingIdx[i], 1);
  result.push({
    id: entry.id,
    participantId: entry.participantId,
    start: mergedStart,
    end: mergedEnd,
    reason: entry.reason,
    createdAt: entry.createdAt,
    anchorAtCreation: entry.anchorAtCreation,
    appliedSwapCount: entry.appliedSwapCount,
  });
  return result;
}
